import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { allLangs, defaultLang } from '../config';
import { getCurrentLanguagePath, addLanguageToPath } from '../utils/languageUtils';

// ----------------------------------------------------------------------

/**
 * Custom hook for language-aware routing
 * Provides current language from URL and utilities for language navigation
 *
 * @returns {Object} Language utilities
 * @returns {Object} currentLang - Current language object {label, value, icon, systemValue}
 * @returns {string} currentLangValue - Current language code ('en' or 'es')
 * @returns {Array} allLangs - All available languages
 * @returns {Function} switchLanguage - Navigate to same page in different language
 * @returns {Function} getLocalizedPath - Add current language prefix to path
 */
export default function useLanguage() {
  const { lang } = useParams(); // Get language from URL parameter
  const navigate = useNavigate();
  const location = useLocation();

  // Find current language object, fallback to default if not found
  const currentLang = allLangs.find(l => l.value === lang) || defaultLang;

  /**
   * Switch to a different language while staying on the same page
   * @param {string} newLang - New language code ('en' or 'es')
   */
  const switchLanguage = (newLang) => {
    const newPath = getCurrentLanguagePath(location.pathname, newLang);
    navigate(newPath, { replace: true });
  };

  /**
   * Add current language prefix to a path
   * @param {string} path - Path without language prefix (e.g., "/blog")
   * @returns {string} - Path with language prefix (e.g., "/en/blog")
   */
  const getLocalizedPath = (path) => {
    return addLanguageToPath(path, lang || defaultLang.value);
  };

  return {
    currentLang,
    currentLangValue: lang || defaultLang.value,
    allLangs,
    switchLanguage,
    getLocalizedPath,
  };
}
